// The state views (drawing v2.6, screen 13; reference section 13): Loading
// (a skeleton in the finished page's shape, never "Loading…" alone), Slow
// (at 4 seconds: still loading, not failed), Failed (a read may say nothing
// changed; the way out is Try again), Unconfirmed (a write that timed out:
// says what to check, never "nothing was lost"), Empty (what would appear
// here and how) and NoAccess (who to ask).

import type { ReactNode } from "react";
import { Clock, Inbox, Lock, RefreshCw, TriangleAlert } from "lucide-react";
import { Button } from "./Button";
import { cx } from "./cx";

export function Skeleton({ width = "100%", height = 12, line = false, className }: { width?: string | number; height?: number; line?: boolean; className?: string }) {
  return <div className={cx("pg-sk", line && "pg-sk--ln", className)} style={{ width, height }} aria-hidden />;
}

/** The same shape as the finished page: a title line, a sentence, a block. */
export function Loading({ label = "Loading", rows = 3 }: { label?: string; rows?: number }) {
  return (
    <div className="pg-card pg-state" role="status" aria-label={label}>
      <Skeleton width="40%" height={12} line />
      <Skeleton width="60%" height={24} line />
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={48} />
      ))}
    </div>
  );
}

/** Four seconds in: still loading, and says so. */
export function Slow({ children = "Still loading. The page is answering, slowly." }: { children?: ReactNode }) {
  return (
    <div className="pg-card pg-empty" role="status">
      <div className="pg-empty__ring">
        <Clock className="pg-ico pg-ico--l" aria-hidden />
      </div>
      <b>Taking longer than usual</b>
      <p>{children}</p>
      <Skeleton width={220} height={10} line className="pg-sk--center" />
    </div>
  );
}

export function Failed({ title = "Couldn't load this", children, onRetry, retryLabel = "Try again" }: { title?: ReactNode; children?: ReactNode; onRetry?: () => void; retryLabel?: string }) {
  return (
    <div className="pg-card pg-empty" role="alert">
      <div className="pg-empty__ring pg-empty__ring--bad">
        <TriangleAlert className="pg-ico pg-ico--l" aria-hidden />
      </div>
      <b>{title}</b>
      <p>{children ?? "Nothing changed. Try again in a moment."}</p>
      {onRetry && (
        <div className="pg-acts">
          <Button icon={<RefreshCw className="pg-ico pg-ico--s" aria-hidden />} onClick={onRetry}>
            {retryLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

/** A write that timed out. Says what to check; never claims nothing was lost. */
export function Unconfirmed({ title = "We don't know if that saved", children, action }: { title?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="pg-card pg-empty" role="alert">
      <div className="pg-empty__ring pg-empty__ring--warn">
        <TriangleAlert className="pg-ico pg-ico--l" aria-hidden />
      </div>
      <b>{title}</b>
      <p>{children}</p>
      {action && <div className="pg-acts">{action}</div>}
    </div>
  );
}

export function Empty({ title, children, action, icon }: { title: ReactNode; children?: ReactNode; action?: ReactNode; icon?: ReactNode }) {
  return (
    <div className="pg-card pg-empty">
      <div className="pg-empty__ring">{icon ?? <Inbox className="pg-ico pg-ico--l" aria-hidden />}</div>
      <b>{title}</b>
      {children && <p>{children}</p>}
      {action && <div className="pg-acts">{action}</div>}
    </div>
  );
}

export function NoAccess({ title = "You can't open this", children, action }: { title?: ReactNode; children: ReactNode; action?: ReactNode }) {
  return (
    <div className="pg-card pg-empty">
      <div className="pg-empty__ring">
        <Lock className="pg-ico pg-ico--l" aria-hidden />
      </div>
      <b>{title}</b>
      <p>{children}</p>
      {action && <div className="pg-acts">{action}</div>}
    </div>
  );
}
